// src/components/common/BottomNav.jsx
// Mobile-only (≤767px) — place this in App.jsx outside <Routes> so it shows on ALL pages
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguagesContext";

/* ─── Icons ─── */
const IconHome = ({ a }) => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill={a?"rgba(0,120,242,0.2)":"none"} stroke={a?"#0078F2":"#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IconStore = ({ a }) => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill={a?"rgba(0,120,242,0.15)":"none"} stroke={a?"#0078F2":"#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);
const IconOrders = ({ a }) => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke={a?"#0078F2":"#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const IconCart = ({ a }) => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill={a?"rgba(0,120,242,0.15)":"none"} stroke={a?"#0078F2":"#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const IconProfile = ({ a }) => (
  <svg width="23" height="23" viewBox="0 0 24 24" fill={a?"rgba(0,120,242,0.15)":"none"} stroke={a?"#0078F2":"#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const CSS = `
.bnav { display: none; }

@media (max-width: 767px) {
  body { padding-bottom: calc(62px + env(safe-area-inset-bottom, 0px)) !important; }

  .bnav {
    display: flex;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 1100;
    height: 62px;
    background: rgba(11,11,11,0.97);
    backdrop-filter: blur(32px);
    -webkit-backdrop-filter: blur(32px);
    border-top: 1px solid rgba(255,255,255,0.07);
    align-items: stretch;
    justify-content: space-around;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .bnav-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-decoration: none;
    color: #555;
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 600;
    padding: 6px 4px 8px;
    -webkit-tap-highlight-color: transparent;
    transition: color 0.15s;
    position: relative;
    letter-spacing: 0.1px;
  }
  .bnav-tab.active { color: #0078F2; }

  /* Active indicator — small pill at top of tab */
  .bnav-tab.active::before {
    content: '';
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 28px; height: 2px;
    background: #0078F2;
    border-radius: 0 0 4px 4px;
  }

  .bnav-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px; height: 24px;
  }

  /* Cart badge */
  .bnav-badge {
    position: absolute;
    top: -5px; right: -7px;
    background: #ef4444;
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 9px; font-weight: 800;
    min-width: 16px; height: 16px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    padding: 0 3px; line-height: 1;
    border: 1.5px solid #0b0b0b;
  }
}
`;

export default function BottomNav({ cartCount = 0 }) {
  const { pathname } = useLocation();
  const { user }     = useAuth();
  const { t }        = useLang();

  const isHome    = pathname === "/";
  const isStore   = pathname === "/store" || pathname.startsWith("/game/");
  const isOrders  = pathname === "/orders";
  const isCart    = pathname === "/cart";
  const isProfile = ["/login", "/register", "/profile"].includes(pathname);

  const label = (key, fallback) => (t && t(key)) || fallback;

  return (
    <>
      <style>{CSS}</style>
      <nav className="bnav" aria-label="Bottom navigation">

        <Link to="/" className={`bnav-tab ${isHome ? "active" : ""}`}>
          <span className="bnav-icon"><IconHome a={isHome} /></span>
          <span>{label("nav_home", "Home")}</span>
        </Link>

        <Link to="/store" className={`bnav-tab ${isStore ? "active" : ""}`}>
          <span className="bnav-icon"><IconStore a={isStore} /></span>
          <span>{label("nav_store", "Store")}</span>
        </Link>

        <Link to={user ? "/orders" : "/login"} className={`bnav-tab ${isOrders ? "active" : ""}`}>
          <span className="bnav-icon"><IconOrders a={isOrders} /></span>
          <span>{label("nav_orders", "Orders")}</span>
        </Link>

        <Link to="/cart" className={`bnav-tab ${isCart ? "active" : ""}`}>
          <span className="bnav-icon">
            <IconCart a={isCart} />
            {cartCount > 0 && (
              <span className="bnav-badge">{cartCount > 99 ? "99+" : cartCount}</span>
            )}
          </span>
          <span>Cart</span>
        </Link>

        <Link to={user ? "/profile" : "/login"} className={`bnav-tab ${isProfile ? "active" : ""}`}>
          <span className="bnav-icon"><IconProfile a={isProfile} /></span>
          <span>Profile</span>
        </Link>

      </nav>
    </>
  );
}